import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, type UpdateBusinessRequest } from '../services/api';
import type {
    BusinessDetailsResponse,
    BusinessTypeMasterDto,
    HolidayClosureDto,
} from '../types/api-responses';
import { queryKeys } from '../lib/queryKeys';
import { getApiErrorMessage, normalizeApiError } from '../types/api-errors';
import { useToast } from '../contexts/ToastContext';
import {
    type BusinessDetailsForm,
    SERVICE_TYPE_TO_SALON_TYPE,
    SALON_TYPE_TO_SERVICE_TYPE,
    defaultBusinessDetailsForm,
} from '../schemas/business.schema';
import { ServiceType } from '../types/enums';

/**
 * AB#1675 Phase A — TanStack Query hooks for the Business Details form.
 *
 * `useBusinessDetailsQuery` fetches the current owner's business profile
 * (or `null` if the user hasn't created one yet — `getMyBusiness` swallows
 * the 404 by design).
 *
 * `useUpdateBusinessMutation` PUTs the BE-supported fields and on success
 * invalidates the `business.me` cache + shows a toast.
 *
 * `mapResponseToForm` and `mapFormToUpdateRequest` are the FE↔BE shape
 * adapters and are exported so the form can re-use them when seeding
 * defaults from `localStorage` for the Phase-B-deferred fields.
 */

/* ── Queries ────────────────────────────────────────────────────────────── */

export function useBusinessDetailsQuery() {
    return useQuery<BusinessDetailsResponse | null>({
        queryKey: queryKeys.business.me(),
        queryFn: () => apiService.getMyBusiness(),
        staleTime: 60_000,
    });
}

/* ── Mutations ──────────────────────────────────────────────────────────── */

export function useUpdateBusinessMutation(businessId: number | null | undefined) {
    const qc = useQueryClient();
    const { showToast } = useToast();

    return useMutation<boolean, Error, UpdateBusinessRequest>({
        mutationFn: async (payload) => {
            if (businessId == null) {
                throw new Error('No business yet — create one before saving changes.');
            }
            return apiService.updateBusiness(businessId, payload);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.business.me() });
            showToast('Business details saved.', 'success');
        },
        onError: (err: unknown) => {
            // AB#1919 (WS-5b)
            const msg = getApiErrorMessage(err, 'Could not save your changes. Please try again.');
            showToast(msg, 'error');
        },
    });
}

/* ── AB#1682 — BusinessTypes master picker (cached server-side 15 min) ── */

export function useBusinessTypesQuery() {
    return useQuery<BusinessTypeMasterDto[]>({
        queryKey: queryKeys.businessTypes.list(),
        queryFn: () => apiService.getBusinessTypes(),
        staleTime: 15 * 60_000, // mirrors the BE IMemoryCache TTL
    });
}

/* ── AB#1682 — Holiday closures CRUD ────────────────────────────────────── */

export function useMyBusinessHolidaysQuery() {
    return useQuery<HolidayClosureDto[]>({
        queryKey: queryKeys.business.holidays(),
        queryFn: () => apiService.getMyBusinessHolidays(),
        staleTime: 30_000,
    });
}

export function useCreateHolidayClosureMutation() {
    const qc = useQueryClient();
    const { showToast } = useToast();
    return useMutation<HolidayClosureDto, Error,
        { fromDate: string; toDate: string; reason: string; source?: string }>({
        mutationFn: (data) => apiService.createBusinessHoliday(data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.business.holidays() });
            qc.invalidateQueries({ queryKey: queryKeys.business.me() });
        },
        onError: (err) => {
            showToast(extractErr(err) || 'Could not add closure.', 'error');
        },
    });
}

export function useUpdateHolidayClosureMutation() {
    const qc = useQueryClient();
    const { showToast } = useToast();
    return useMutation<HolidayClosureDto, Error,
        { id: number; fromDate: string; toDate: string; reason: string; source?: string }>({
        mutationFn: ({ id, ...data }) => apiService.updateBusinessHoliday(id, data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.business.holidays() });
            qc.invalidateQueries({ queryKey: queryKeys.business.me() });
        },
        onError: (err) => {
            showToast(extractErr(err) || 'Could not update closure.', 'error');
        },
    });
}

export function useDeleteHolidayClosureMutation() {
    const qc = useQueryClient();
    const { showToast } = useToast();
    return useMutation<boolean, Error, number>({
        mutationFn: (id) => apiService.deleteBusinessHoliday(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.business.holidays() });
            qc.invalidateQueries({ queryKey: queryKeys.business.me() });
        },
        onError: (err) => {
            showToast(extractErr(err) || 'Could not remove closure.', 'error');
        },
    });
}

function extractErr(err: unknown): string | undefined {
    // AB#1919 (WS-5b) — prefer the 7807 `detail`, fall back to legacy `message`
    // via the normalizer; undefined when neither is present.
    const { detail } = normalizeApiError(err);
    if (detail) return detail;
    return err instanceof Error ? err.message : undefined;
}

/* ── Adapters ───────────────────────────────────────────────────────────── */

interface ApiBusinessHourRow {
    // AB#2001 — the BE wires DayOfWeek as the enum NAME ("Sunday".."Saturday")
    // via the global JsonStringEnumConverter (AB#1894 convention) on
    // GET /business/my-business, and as the 0..6 int on other controllers.
    // Widened to both; normalizeDayOfWeek() coerces to int at the boundary.
    dayOfWeek?: number | string;
    isOpen?: boolean;
    openTime?: string | null;
    closeTime?: string | null;
    is24Hours?: boolean;
}

/**
 * AB#2001 — coerce a BusinessHours `dayOfWeek` to its 0..6 int at the read
 * boundary. The BE serializes System.DayOfWeek as the enum NAME
 * ("Sunday".."Saturday") via the global JsonStringEnumConverter (AB#1894),
 * but the form indexes hours by int (Map key + scaffold order). Idempotent:
 * ints pass through; enum names map; numeric strings parse; anything else
 * returns -1 — a non-day sentinel that the 0..6 `hoursByDay.get()` lookup
 * naturally ignores rather than clobbering a real day. Matches the shape
 * Faris named (`useBranchHours.ts` / `useBusinessHoursMatrix.ts`, -1 default)
 * plus the numeric-string branch from `ScheduleTab.tsx` — the 3 ratified
 * precedents. Exported for unit coverage; a shared helper is deferred
 * tech-debt (arch-brief §6, rule-of-three).
 */
const DAY_NAME_TO_INDEX: Record<string, number> = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
};

export function normalizeDayOfWeek(dow: string | number): number {
    if (typeof dow === 'number') return dow;
    const byName = DAY_NAME_TO_INDEX[dow];
    if (typeof byName === 'number') return byName;
    const parsed = parseInt(dow, 10);
    return Number.isNaN(parsed) ? -1 : parsed;
}

/** Convert the BE response into our strict form shape. Missing / partial
 *  fields fall back to the form's defaults so the form is always fully
 *  populated (RHF requires defined defaults for controlled inputs).
 *
 *  AB#1682 (Phase B FE) — now round-trips bilingual fields, contact
 *  channels, social links, business types (by NameEn — the picker UI key),
 *  holiday closures, and the booking-settings block. The Country picker
 *  still uses ISO-2 strings; CountryId/CurrencyId resolution stays
 *  server-side until the FE gains a Countries master fetch.
 */
export function mapResponseToForm(
    res: BusinessDetailsResponse | null | undefined,
): BusinessDetailsForm {
    const fallback = defaultBusinessDetailsForm();
    if (!res) return fallback;

    const location = res.location ?? {};
    // Prefer the new resolved CountryIso2 if BE supplied it (AB#1682);
    // fall back to legacy free-text Location.Country for older rows.
    const countryRaw = res.countryIso2 ?? location.country ?? '';
    const country = typeof countryRaw === 'string' && countryRaw.length >= 2
        ? countryRaw.slice(0, 2).toUpperCase()
        : fallback.country;

    // BE serviceType comes back as the enum NAME ("Everyone") in some
    // controllers and as the numeric value (3) in others. Handle both.
    let serviceType: ServiceType = fallback.serviceType;
    if (typeof res.serviceType === 'number') {
        serviceType = res.serviceType as ServiceType;
    } else if (typeof res.serviceType === 'string') {
        const byName = (ServiceType as unknown as Record<string, number>)[res.serviceType];
        if (typeof byName === 'number') serviceType = byName as ServiceType;
    }

    // AB#2001 — normalize the wire dayOfWeek (enum name "Sunday" or int) to
    // its 0..6 int before using it as the Map key. Previously this guarded
    // `typeof === 'number'`, which dropped every string-wired row → the form
    // fell back to the all-open 09:00–21:00 scaffold and Save then silently
    // overwrote the salon's real hours. Mirrors the serviceType string|number
    // handling directly above.
    const hoursByDay = new Map<number, ApiBusinessHourRow>();
    for (const row of res.businessHours ?? []) {
        const r = row as ApiBusinessHourRow;
        if (r.dayOfWeek == null) continue;
        hoursByDay.set(normalizeDayOfWeek(r.dayOfWeek), r);
    }
    const businessHours = fallback.businessHours.map((d) => {
        const r = hoursByDay.get(d.dayOfWeek);
        if (!r) return d;
        return {
            dayOfWeek: d.dayOfWeek,
            isOpen: r.isOpen ?? d.isOpen,
            openTime: r.openTime ?? d.openTime,
            closeTime: r.closeTime ?? d.closeTime,
            is24Hours: r.is24Hours ?? d.is24Hours,
        };
    });

    // BusinessTypes — the picker uses NameEn as the key for both display
    // and form state; mapFormToUpdateRequest converts back to ids on Save.
    const businessTypes = (res.businessTypes ?? []).map(t => t.nameEn);

    // Holidays — convert BE shape to form shape (id stays as-is so an
    // edit issues a PUT, an add issues a POST, etc.).
    const holidays = (res.holidayClosures ?? []).map(c => ({
        id: String(c.id),
        from: c.fromDate,
        to: c.toDate,
        reason: c.reason ?? '',
    }));

    return {
        ...fallback,
        name: res.name ?? '',
        about: res.about ?? '',
        nameAr: res.nameAR ?? '',
        aboutAr: res.aboutAR ?? '',
        email: res.email ?? '',
        phone: res.phone ?? '',
        website: res.website ?? '',
        country,
        serviceType,
        businessHours,
        businessTypes,
        holidays,
        social: {
            facebook: location.facebook ?? '',
            twitter: location.twitter ?? '',
            instagram: location.instagram ?? '',
            whatsapp: location.whatsApp ?? '',
            tiktok: location.tikTok ?? '',
            googleMaps: location.googleMapsUrl ?? '',
        },
        settings: {
            salonType: SERVICE_TYPE_TO_SALON_TYPE[serviceType] ?? fallback.settings.salonType,
            decimalDigits: ((d: number | undefined) =>
                d === 0 || d === 2 || d === 3 ? (d as 0 | 2 | 3) : 2)(res.settings?.pricingDecimalPlaces),
            autoConfirmBookings: res.settings?.autoConfirmBookings ?? fallback.settings.autoConfirmBookings,
            allowWalkIns: res.settings?.allowWalkIns ?? fallback.settings.allowWalkIns,
            showRatingsPublicly: res.settings?.showRatings ?? fallback.settings.showRatingsPublicly,
        },
    };
}

/** Build the BE PUT payload from the form. AB#1682 — now sends every
 *  Phase B field. `businessTypeIds` resolution requires the master picker
 *  list (so we know NameEn → id), so the caller must pass it in. If the
 *  master isn't loaded yet, businessTypeIds is omitted (BE treats null as
 *  "no change", preserving whatever the salon already has).
 *
 *  AB#1682 hotfix — the BE's existing UpdateBusinessRequest has `[Required]`
 *  data annotations on `Location.City/State/ZipCode/StreetAddress` and a
 *  non-empty `CategoryIds` + a full 7-day `BusinessHours` array. None of
 *  these are surfaced in BusinessDetailsSection's UI today (they belonged
 *  to the legacy 5-step onboarding wizard). To stop a Save click from
 *  failing 400 validation, we pass through the existing BE values via
 *  the optional `original` parameter. The proper architectural fix —
 *  making those fields optional on Update — lands as part of the BE
 *  follow-up PBI (separate to keep this hotfix small).
 */
export function mapFormToUpdateRequest(
    form: BusinessDetailsForm,
    businessTypesMaster?: BusinessTypeMasterDto[],
    original?: BusinessDetailsResponse | null,
): UpdateBusinessRequest {
    const nameToId = new Map<string, number>();
    for (const t of businessTypesMaster ?? []) nameToId.set(t.nameEn, t.id);

    const businessTypeIds: number[] | undefined = businessTypesMaster
        ? form.businessTypes
            .map(name => nameToId.get(name))
            .filter((v): v is number => typeof v === 'number')
        : undefined;

    // Pass-through: city / state / zipCode / streetAddress / categoryIds
    // come from the original GET (the form doesn't manage them), so the
    // BE's [Required] validators stay happy without us forcing the user
    // to re-enter address fields they never saw.
    const origLoc = (original?.location ?? {}) as {
        city?: string | null;
        state?: string | null;
        streetAddress?: string | null;
        zipCode?: string | null;
        hasHomeService?: boolean;
        latitude?: number | null;
        longitude?: number | null;
    };
    const origCategoryIds: number[] = ((original?.categories ?? []) as Array<{ categoryId?: number }>)
        .map(c => c.categoryId)
        .filter((id): id is number => typeof id === 'number');

    return {
        name: form.name,
        about: form.about,
        // Salon Type select is the source of truth for ServiceType — re-derive
        // here so saving honours whatever the user picked in "Other settings"
        // even if they never touched the legacy serviceType field.
        serviceType: SALON_TYPE_TO_SERVICE_TYPE[form.settings.salonType] ?? form.serviceType,
        // AB#1682 — bilingual + direct contact (always send; BE persists
        // empty strings as NULL via column nullability).
        nameAR: form.nameAr,
        aboutAR: form.aboutAr,
        email: form.email,
        phone: form.phone,
        website: form.website,
        // AB#1682 — multi-type business types (max 4 enforced BE-side).
        businessTypeIds,
        // AB#1682 — booking config + display preferences.
        settings: {
            pricingDecimalPlaces: form.settings.decimalDigits,
            autoConfirmBookings: form.settings.autoConfirmBookings,
            allowWalkIns: form.settings.allowWalkIns,
            showRatings: form.settings.showRatingsPublicly,
        },
        // Country left as legacy string in the location DTO; CountryId
        // resolution is server-side until the FE gains a Countries fetch.
        // AB#1682 hotfix — pass through city/state/zipCode/streetAddress
        // from the original GET so BE [Required] validators stay happy.
        // Empty-string defaults provide a sane fallback for fresh businesses
        // (BE will still reject those with a helpful error rather than the
        // current cryptic "field required" — handled by the Phase-B BE
        // follow-up that makes these optional on Update).
        location: {
            country: form.country,
            city: origLoc.city ?? '',
            state: origLoc.state ?? '',
            streetAddress: origLoc.streetAddress ?? '',
            zipCode: origLoc.zipCode ?? '',
            hasHomeService: origLoc.hasHomeService ?? false,
            latitude: origLoc.latitude ?? undefined,
            longitude: origLoc.longitude ?? undefined,
            // AB#1682 — social channels persist on BusinessLocation.
            facebook: form.social.facebook,
            twitter: form.social.twitter,
            instagram: form.social.instagram,
            whatsApp: form.social.whatsapp,
            tikTok: form.social.tiktok,
            googleMapsUrl: form.social.googleMaps,
        },
        businessHours: form.businessHours.map((d) => ({
            dayOfWeek: d.dayOfWeek,
            isOpen: d.isOpen,
            openTime: d.isOpen ? d.openTime : undefined,
            closeTime: d.isOpen ? d.closeTime : undefined,
            is24Hours: d.is24Hours,
        })),
        // AB#1682 hotfix — BE rejects empty CategoryIds. Echo the existing
        // selection so a Save click doesn't accidentally clear it.
        categoryIds: origCategoryIds,
    };
}

/* ── Local-draft persistence for Phase-B-deferred fields ────────────────── */

const DRAFT_KEY = 'devanza:business-details:draft';

interface LocalDraft {
    nameAr: string;
    aboutAr: string;
    email: string;
    phone: string;
    website: string;
    businessTypes: string[];
    holidays: BusinessDetailsForm['holidays'];
    social: BusinessDetailsForm['social'];
    settings: BusinessDetailsForm['settings'];
}

function pickLocalDraft(form: BusinessDetailsForm): LocalDraft {
    return {
        nameAr: form.nameAr,
        aboutAr: form.aboutAr,
        email: form.email,
        phone: form.phone,
        website: form.website,
        businessTypes: form.businessTypes,
        holidays: form.holidays,
        social: form.social,
        settings: form.settings,
    };
}

export function loadLocalDraft(businessId: number | null | undefined): Partial<LocalDraft> | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(`${DRAFT_KEY}:${businessId ?? 'new'}`);
        return raw ? JSON.parse(raw) as Partial<LocalDraft> : null;
    } catch {
        return null;
    }
}

export function saveLocalDraft(
    businessId: number | null | undefined,
    form: BusinessDetailsForm,
): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(
            `${DRAFT_KEY}:${businessId ?? 'new'}`,
            JSON.stringify(pickLocalDraft(form)),
        );
    } catch {
        /* quota exhausted / private browsing — silently no-op */
    }
}

/** Tiny debounced wrapper around `saveLocalDraft` for use inside form
 *  watchers. Returns a stable function that schedules saves at most once
 *  per `delayMs`. */
export function useDebouncedSaveLocalDraft(
    businessId: number | null | undefined,
    delayMs = 400,
): (form: BusinessDetailsForm) => void {
    return useCallback((form: BusinessDetailsForm) => {
        const w = window as unknown as { __devanzaDraftTimer?: ReturnType<typeof setTimeout> };
        if (w.__devanzaDraftTimer) clearTimeout(w.__devanzaDraftTimer);
        w.__devanzaDraftTimer = setTimeout(() => saveLocalDraft(businessId, form), delayMs);
    }, [businessId, delayMs]);
}
